"use client"
import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, ChevronUp } from 'lucide-react'
import slugify from 'slugify'
import { Content } from 'mdast'
import { ChunkDocContext } from '@/lib/parseMarkdown'
import Markdown, { type FootnoteHandler } from '../Markdown'

type BranchpointBoxProps = {
  children: Content[]
  docContext?: ChunkDocContext
  FootnoteHandler?: FootnoteHandler
}

const getText = (node: any): string => {
  if (!node) return ''
  if (node.value) return node.value
  if (node.children) return node.children.map(getText).join('')
  return ''
}

// Splits a `:::branchpoint` container's children into (title, summaryNodes,
// bodyNodes). The current authoring shape nests a `:::branchSummary` as the
// first child whose first item is a `## Title` heading and whose remaining
// items are the summary preview. Everything after the branchSummary close is
// the expanded body.
const splitBranchpointChildren = (
  children: Content[],
): { title: string; summaryNodes: Content[]; bodyNodes: Content[] } => {
  const firstChild = children[0] as any
  if (firstChild?.type === 'containerDirective' && firstChild.name === 'branchSummary') {
    const inner = (firstChild.children || []) as Content[]
    const headingIdx = inner.findIndex(c => (c as any).type === 'heading')
    const heading = headingIdx === -1 ? undefined : (inner[headingIdx] as any)
    const title = heading ? getText(heading).trim() : ''
    const summaryNodes = headingIdx === -1 ? inner : inner.slice(headingIdx + 1)
    return { title, summaryNodes, bodyNodes: children.slice(1) }
  }
  // Legacy / fallback: a leading `## Heading` directly under :::branchpoint.
  const headingIdx = children.findIndex(c => (c as any).type === 'heading')
  if (headingIdx !== -1) {
    const title = getText(children[headingIdx]).trim()
    return { title, summaryNodes: [], bodyNodes: children.slice(headingIdx + 1) }
  }
  return { title: '', summaryNodes: [], bodyNodes: children }
}

// Renders a `:::branchpoint` block as an inline expandable box. The summary
// (from the nested `:::branchSummary`) shows by default; clicking the toggle
// reveals the full alternate-timeline body in place. Each box is registered
// in the playbook's anchor index under `branchpoint-<title-slug>`, so a URL
// hash like `#branchpoint-mutually-assured-compute-destruction` will
// auto-expand the right Plan A path (via useAnchorDeepLink) and the box
// itself defaults to expanded when the page loads with that hash.
export const BranchpointBox = ({
  children,
  docContext,
  FootnoteHandler,
}: BranchpointBoxProps) => {
  const { title, summaryNodes, bodyNodes } = useMemo(
    () => splitBranchpointChildren(children),
    [children],
  )

  const elementId = useMemo(
    () => (title ? `branchpoint-${slugify(title, { lower: true, strict: true })}` : undefined),
    [title],
  )

  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window === 'undefined' || !elementId) return false
    const hash = window.location.hash
    if (!hash || hash.length <= 1) return false
    try {
      return decodeURIComponent(hash.slice(1)) === elementId
    } catch {
      return hash.slice(1) === elementId
    }
  })

  // Expand when the URL hash becomes our id at runtime (not just on first
  // mount). The end-of-Plan-A timeline diagram navigates to a branchpoint by
  // firing a hashchange — this is how an already-mounted box opens in response.
  // We only force-open (never auto-close) so a hashchange elsewhere on the page
  // can't collapse a box the reader opened by hand.
  useEffect(() => {
    if (!elementId) return
    const openIfTargeted = () => {
      const hash = window.location.hash
      if (!hash || hash.length <= 1) return
      const key = (() => {
        try { return decodeURIComponent(hash.slice(1)) } catch { return hash.slice(1) }
      })()
      if (key === elementId) setIsExpanded(true)
    }
    window.addEventListener('hashchange', openIfTargeted)
    return () => window.removeEventListener('hashchange', openIfTargeted)
  }, [elementId])

  // Update URL hash when toggling, so the open/closed state is shareable.
  // Uses replaceState (no hashchange event) so we don't re-trigger the
  // deep-link scroll/stabilize loop on the same page.
  const toggleExpanded = () => {
    setIsExpanded(v => {
      const next = !v
      if (typeof window !== 'undefined' && elementId) {
        const url = new URL(window.location.href)
        if (next) {
          url.hash = elementId
        } else {
          const current = (() => {
            try { return decodeURIComponent(url.hash.slice(1)) } catch { return url.hash.slice(1) }
          })()
          if (current === elementId) url.hash = ''
        }
        window.history.replaceState({}, '', url.toString())
      }
      return next
    })
  }

  return (
    <div id={elementId} className="my-12 rounded-lg border-2 border-[#232323] px-6 py-4 scroll-mt-20">
      <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#8b0000]">Alternate Timeline</div>
      {title && <h2 className="!mt-1 text-[35px]">{title}</h2>}
      {summaryNodes.length > 0 && (
        <div className="mt-2 leading-relaxed">
          <Markdown nodes={summaryNodes} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </div>
      )}
      <button
        type="button"
        onClick={toggleExpanded}
        className="mt-3 mb-1 inline-flex items-center gap-1 italic text-[#8b0000] cursor-pointer hover:underline"
      >
        {isExpanded ? <ChevronUp size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
        {isExpanded ? 'Close timeline' : 'Read this timeline'}
      </button>
      {isExpanded && bodyNodes.length > 0 && (
        <div className="mt-6 border-t border-black/20 pt-4 -mx-6 px-6">
          <Markdown nodes={bodyNodes} docContext={docContext} FootnoteHandler={FootnoteHandler} />
        </div>
      )}
    </div>
  )
}
